import { Button } from "@react-md/button";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import api from "../../shared/api";
import { EMAIL_PATTERN, TextArea, TextInput, useField } from "../shared/form";
import { useT } from "../../i18n";

import styles from "./home.module.scss";
import variables from "../../styles/variables.module.scss";

/** DOM id of the support block; the header/footer "Support" entries scroll here. */
export const SUPPORT_ANCHOR = "support";

const Support = () => {
  const t = useT();
  const [support, setSupport] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  // "/?support=true" (header menu, footer) opens the form and scrolls to it.
  // scrollIntoView walks every scrollable ancestor, so it works with the
  // page's own scroll container. Repeat a few times because the partners
  // carousel shifts the layout as its images load, then drop the param so a
  // second tap on "Support" re-triggers the scroll.
  useEffect(() => {
    if (!new URLSearchParams(location.search).has("support")) return;
    setSupport(true);
    const timers = [100, 500, 1100, 1900].map((delay, i, all) =>
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        if (i === all.length - 1) navigate("/", { replace: true });
      }, delay),
    );
    return () => timers.forEach(clearTimeout);
  }, [location.search, location.key, navigate]);

  const [sending, setSending] = useState(false);
  const [messageType, setMessageType] = useState<"success" | "error" | null>(
    null,
  );

  const email = useField({ required: true, pattern: EMAIL_PATTERN });
  const message = useField({ required: true, minLength: 4 });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    email.touch();
    message.touch();
    // Guard against double clicks while the request is in flight — on a slow
    // connection each extra click was creating a duplicate support ticket.
    if (email.invalid || message.invalid || sending) return;
    setSending(true);
    try {
      await api.post(`/support`, { email: email.value.trim(), message: message.value });
      setMessageType("success");
      email.reset();
      message.reset();
    } catch (err) {
      setMessageType("error");
    } finally {
      setSending(false);
    }
    setTimeout(() => {
      setMessageType(null);
    }, 5000);
  };

  return (
    <div ref={ref} id={SUPPORT_ANCHOR} className="w-full scroll-mt-4">
      {support ? (
        <form
          className={`${variables.row} ${styles.support}`}
          onSubmit={submit}
          noValidate
        >
          <span className={variables.title}>{t("support.title")}</span>
          {messageType && (
            <span
              className={variables.textSm}
              style={{ color: messageType === "success" ? "green" : "red" }}
            >
              {messageType === "success"
                ? t("support.success")
                : t("common.somethingWrong")}
            </span>
          )}
          <TextInput
            id="support-email"
            type="email"
            autoComplete="email"
            label={t("support.email")}
            error={email.error}
            {...email.props}
          />
          <TextArea
            id="support-message"
            label={t("support.message")}
            error={message.error}
            {...message.props}
          />
          <Button
            type="submit"
            className={variables.containedBtn}
            themeType="contained"
            disabled={sending}
          >
            {sending ? t("common.sending") : t("common.send")}
          </Button>
        </form>
      ) : (
        <span
          className={`${variables.textXs} ${styles.supportTitle}`}
          onClick={() => setSupport(true)}
        >
          {t("support.link")}
        </span>
      )}
    </div>
  );
};

export default Support;
